import React, { useState } from 'react';
import {
    List,
    useDataProvider,
    useNotify,
    useRefresh,
    useListContext,
} from 'react-admin';
import {
    Box,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Paper,
} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';

const formatDateTime = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString('ru-RU');
};

const formatDateTimeWithoutShift = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    // last_login_date приходит как timestamp без timezone контекста устройства.
    // Показываем значение без локального смещения браузера.
    return date.toLocaleString('ru-RU', { timeZone: 'UTC' });
};

const APPEAL_TYPE_LABELS = {
    technical: 'Техническое обращение',
    zoo: 'Обращение к Зоопарку',
};

const maskPhone = (phone) => {
    if (!phone) return '—';
    const digits = String(phone).replace(/\D/g, '');
    if (digits.length >= 11 && digits.startsWith('79')) {
        return `+79*****${digits.slice(-4)}`;
    }
    if (digits.length >= 6) {
        return `+${digits.slice(0, 2)}*****${digits.slice(-4)}`;
    }
    return phone;
};

const GuestCards = ({ onOpen }) => {
    const { data = [], isPending } = useListContext();
    if (isPending) return <Box sx={{ py: 8, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;
    return <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>{data.map((record) => (
        <Paper key={record.id} component="button" type="button" onClick={() => onOpen(record)} elevation={0} sx={{ p: { xs: 2, md: 2.5 }, border: '1px solid', borderColor: record.has_unread ? 'error.light' : 'divider', borderRadius: 3, bgcolor: record.has_unread ? 'rgba(244,67,54,.04)' : 'background.paper', color: 'inherit', font: 'inherit', textAlign: 'left', cursor: 'pointer', transition: '160ms ease', '&:hover': { borderColor: 'primary.main', transform: 'translateY(-2px)', boxShadow: '0 12px 30px rgba(15,23,42,.08)' }, '&:focus-visible': { outline: '3px solid', outlineColor: 'primary.light', outlineOffset: 2 } }}>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}><Box sx={{ width: 48, height: 48, borderRadius: 2.5, bgcolor: 'action.hover', display: 'grid', placeItems: 'center', color: 'primary.main', flexShrink: 0 }}><PersonRoundedIcon /></Box><Box sx={{ minWidth: 0, flex: 1 }}><Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between', alignItems: 'center' }}><Typography variant="h6" noWrap sx={{ fontWeight: 800 }}>{record.name || 'Гость без имени'}</Typography>{record.unread_count > 0 && <Chip size="small" label={`${record.unread_count} новых`} color="error" />}</Box><Typography variant="caption" color="text.secondary">ID {record.id}</Typography></Box></Box>
            <Box sx={{ display: 'grid', gap: .8, mt: 2 }}><Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}><PhoneOutlinedIcon sx={{ fontSize: 18, color: 'text.disabled' }} /><Typography variant="body2">{maskPhone(record.phone)}</Typography></Box><Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}><EmailOutlinedIcon sx={{ fontSize: 18, color: 'text.disabled' }} /><Typography variant="body2" noWrap>{record.email || 'Email не указан'}</Typography></Box></Box>
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center' }}><Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}><ForumRoundedIcon color={record.has_unread ? 'error' : 'action'} fontSize="small" /><Typography variant="body2" sx={{ fontWeight: 750 }}>{record.appeals_count ?? 0} обращений</Typography></Box><Typography variant="caption" color="text.secondary">Вход: {formatDateTimeWithoutShift(record.last_login_date)}</Typography></Box>
        </Paper>
    ))}</Box>;
};

export const GuestAppealsList = () => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [userAppeals, setUserAppeals] = useState([]);
    const [appealTypeFilter, setAppealTypeFilter] = useState('all');
    const [loadingAppeals, setLoadingAppeals] = useState(false);

    const openAppealsDialog = async (record) => {
        setSelectedUser(record);
        setDialogOpen(true);
        setAppealTypeFilter('all');
        setLoadingAppeals(true);

        try {
            const result = await dataProvider.getList('guest-appeals', {
                pagination: { page: 1, perPage: 1000 },
                sort: { field: 'created_at', order: 'DESC' },
                filter: { user_id: record.id },
            });

            const appeals = result.data || [];
            setUserAppeals(appeals);

            const hasUnread = appeals.some((appeal) => !appeal.is_read);
            if (hasUnread) {
                await dataProvider.create('guest-appeals-mark-read', {
                    data: { user_id: record.id },
                });

                setUserAppeals((prev) => prev.map((appeal) => ({ ...appeal, is_read: true })));
                refresh();
            }
        } catch (error) {
            console.error('Error loading user appeals:', error);
            notify(`Ошибка загрузки обращений: ${error.message}`, { type: 'error' });
            setUserAppeals([]);
        } finally {
            setLoadingAppeals(false);
        }
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setSelectedUser(null);
        setUserAppeals([]);
        setAppealTypeFilter('all');
    };

    const filteredAppeals = userAppeals.filter((appeal) => (
        appealTypeFilter === 'all' ? true : appeal.appeal_type === appealTypeFilter
    ));

    return (
        <>
            <List
                title="Гости и обращения"
                resource="guest-users"
                perPage={30}
                sort={{ field: 'appeals_count', order: 'DESC' }}
                exporter={false}
                sx={{ '& .RaList-content': { boxShadow: 'none', bgcolor: 'transparent' } }}
            >
                <Box sx={{ mb: 3 }}><Typography variant="h4" sx={{ fontWeight: 850, letterSpacing: '-.03em', mb: .75 }}>Гости и обращения</Typography><Typography color="text.secondary">Карточки посетителей, история обращений и непрочитанные сообщения.</Typography></Box>
                <GuestCards onOpen={openAppealsDialog} />
            </List>

            <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 850 }}>{selectedUser ? `Обращения · ${selectedUser.name || selectedUser.phone || selectedUser.id}` : 'Обращения пользователя'}</DialogTitle>
                <DialogContent dividers>
                    {loadingAppeals ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
                            <CircularProgress size={22} />
                            <Typography>Загружаем обращения...</Typography>
                        </Box>
                    ) : userAppeals.length === 0 ? (
                        <Typography color="text.secondary">Обращений пока нет</Typography>
                    ) : (
                        <Box sx={{ display: 'grid', gap: 1.5 }}>
                            <FormControl size="small" sx={{ maxWidth: 320 }}>
                                <InputLabel id="appeal-type-filter-label">Тип обращения</InputLabel>
                                <Select
                                    labelId="appeal-type-filter-label"
                                    value={appealTypeFilter}
                                    label="Тип обращения"
                                    onChange={(event) => setAppealTypeFilter(event.target.value)}
                                >
                                    <MenuItem value="all">Все обращения</MenuItem>
                                    <MenuItem value="technical">Техническое обращение</MenuItem>
                                    <MenuItem value="zoo">Обращение к Зоопарку</MenuItem>
                                </Select>
                            </FormControl>

                            {filteredAppeals.length === 0 && (
                                <Typography color="text.secondary">
                                    Обращений выбранного типа пока нет
                                </Typography>
                            )}

                            {filteredAppeals.map((appeal) => (
                                <Box
                                    key={appeal.id}
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1.5,
                                        p: 2,
                                        backgroundColor: 'background.paper',
                                    }}
                                >
                                    <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                                        {appeal.name || 'Без имени'} ({appeal.email || 'Без email'})
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                        Тип: {APPEAL_TYPE_LABELS[appeal.appeal_type] || appeal.appeal_type || '—'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                        {formatDateTime(appeal.created_at)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {appeal.message}
                                    </Typography>
                                    {Array.isArray(appeal.media_urls) && appeal.media_urls.length > 0 && (
                                        <Box sx={{ mt: 1.5, display: 'grid', gap: 0.5 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                Медиафайлы:
                                            </Typography>
                                            {appeal.media_urls.map((url) => (
                                                <a
                                                    key={url}
                                                    href={url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    style={{ fontSize: '0.8rem', wordBreak: 'break-all' }}
                                                >
                                                    {url}
                                                </a>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog}>Закрыть</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
